import React, { useState } from 'react';
import axios from 'axios';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Copy, Star, ThumbsUp, ThumbsDown, Check, Image as ImageIcon, Download, Share2, Archive, Volume2, Sparkles } from 'lucide-react';
import MessageItem from './MessageItem';
import EmptyState from './EmptyState';
import ResearchLoadingAnimation from './ResearchLoadingAnimation';
import { showToast } from '@/utils/toast';

/**
 * ChatMessages Component
 * Renders message list with typing indicator and message actions
 * 
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {boolean} props.isTyping - Zeha is typing indicator
 * @param {Array} props.favorites - Array of favorite message IDs
 * @param {boolean} props.isKidsUser - Kids theme active
 * @param {string} props.searchQuery - Active search query
 * @param {Function} props.onCopyMessage - Copy message handler
 * @param {Function} props.onToggleFavorite - Toggle favorite handler
 * @param {Function} props.onGiveFeedback - Give feedback handler
 * @param {Function} props.onImageClick - Image click handler
 * @param {Function} props.onReplayAudio - Replay audio handler
 * @param {Object} props.messageAudioChunks - Audio chunks by message ID
 * @param {string} props.isReplaying - Currently replaying message ID
 * @param {React.RefObject} props.messagesEndRef - Ref for auto-scroll
 */
function ChatMessages({
  messages,
  isTyping,
  favorites,
  isKidsUser,
  searchQuery,
  onCopyMessage,
  onToggleFavorite,
  onGiveFeedback,
  onImageClick,
  onResearchCancel,
  onReplayAudio,
  messageAudioChunks,
  isReplaying,
  messagesEndRef,
  onSuggestionClick
}) {
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  
  /**
   * Handle copy message
   */
  const handleCopy = (content, messageId) => {
    navigator.clipboard.writeText(content);
    setCopiedMessageId(messageId);
    showToast.success('Kopyalandı!');
    
    // Reset copied state after 2 seconds
    setTimeout(() => {
      setCopiedMessageId(null);
    }, 2000);
    
    if (onCopyMessage) {
      onCopyMessage(content, messageId);
    }
  };
  
  /**
   * Check if message is a research report
   */
  const isResearchReport = (message) => {
    return message.content && message.content.includes('Derin Araştırma Sonucu');
  };
  
  /**
   * Download research report as PDF
   */
  const handleDownloadPDF = async (message) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `/api/research/download-pdf/${message.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('PDF oluşturulamadı');
      }
      
      // Download PDF
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zeha-arastirma-${message.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast.success('PDF indirildi!');
    } catch (error) {
      console.error('PDF download error:', error);
      showToast.error('PDF indirilemedi');
    }
  };
  
  /**
   * Share research report
   */
  const handleShare = async (message) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Zeha AI - Araştırma Raporu',
          text: message.content.substring(0, 200) + '...',
          url: window.location.href
        });
        showToast.success('Paylaşıldı!');
      } else {
        await navigator.clipboard.writeText(window.location.href);
        showToast.success('Link kopyalandı!');
      }
    } catch (error) {
      showToast.error('Paylaşım başarısız');
    }
  };
  
  /**
   * Archive research report
   */
  const handleArchive = async (message) => {
    try {
      const token = localStorage.getItem('access_token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const guestId = user.id || '';
      
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (guestId) headers['X-Guest-Id'] = guestId;
      
      await axios.post(
        `/api/favorites?message_id=${encodeURIComponent(message.id)}&message_content=${encodeURIComponent((message.content || '').substring(0, 500))}&session_id=${encodeURIComponent(message.session_id || 'unknown')}&assistant_response=${encodeURIComponent((message.content || '').substring(0, 1000))}`,
        null,
        { headers }
      );
      showToast.success('Arşivlendi!');
    } catch (error) {
      console.error('Archive error:', error);
      showToast.error('Arşivlenemedi');
    }
  };
  
  /**
   * Check if message is favorited
   */
  const isFavorited = (messageId) => {
    return favorites?.some(f => f.message_id === messageId);
  };
  
  /**
   * Highlight search results
   */
  const highlightText = (text, query) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={index} className="bg-yellow-200 dark:bg-yellow-800">{part}</mark>
        : part
    );
  };
  
  return (
    <ScrollArea className="flex-1">
      <div className="max-w-6xl mx-auto space-y-6 px-4 py-6">
        {messages.length === 0 && (
          <EmptyState onSuggestionClick={onSuggestionClick} />
        )}
        {messages.map((message) => {
          const isFav = isFavorited(message.id);
          const isAssistant = message.role === 'assistant';
          
          // Special rendering for research loading animation
          if (message.isResearchLoading && message.content === 'RESEARCH_LOADING_ANIMATION') {
            return (
              <div key={message.id}>
                <ResearchLoadingAnimation onCancel={onResearchCancel} />
              </div>
            );
          }
          
          return (
            <div key={message.id} className="group relative">
              <MessageItem 
                message={{
                  ...message,
                  content: searchQuery ? highlightText(message.content, searchQuery) : message.content
                }}
                onImageClick={onImageClick}
                onReplayAudio={onReplayAudio}
                hasAudio={messageAudioChunks && messageAudioChunks[message.id]}
                isReplaying={isReplaying === message.id}
              />
              
              {/* Message Actions (only for assistant messages) */}
              {isAssistant && (
                <div className="flex items-center gap-0.5 mt-1.5 ml-11 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    onClick={() => handleCopy(message.content, message.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Kopyala"
                  >
                    {copiedMessageId === message.id ? (
                      <><Check className="h-3 w-3 text-green-500" /> <span className="text-green-500">Kopyalandı</span></>
                    ) : (
                      <><Copy className="h-3 w-3" /> Kopyala</>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleArchive(message)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${isFav ? 'text-purple-500 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    title="Kaydet"
                  >
                    <Star className={`h-3 w-3 ${isFav ? 'fill-current' : ''}`} /> {isFav ? 'Kaydedildi' : 'Kaydet'}
                  </button>
                  
                  <button
                    onClick={() => handleShare(message)}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Paylaş"
                  >
                    <Share2 className="h-3 w-3" /> Paylaş
                  </button>
                  
                  <div className="w-px h-4 bg-gray-200 dark:bg-gray-600 mx-1" />
                  
                  <button
                    onClick={() => onGiveFeedback && onGiveFeedback(message.id, 'positive')}
                    className="p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                    title="İyi cevap"
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  
                  <button
                    onClick={() => onGiveFeedback && onGiveFeedback(message.id, 'negative')}
                    className="p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Kötü cevap"
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-red-500">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-2.5 px-4 py-3 rounded-2xl bg-gray-100 dark:bg-gray-700 rounded-tl-none">
                <div className="flex space-x-1">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500">Zeha düşünüyor...</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Auto-scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
}

// Wrap with React.memo with custom comparison
export default React.memo(ChatMessages, (prevProps, nextProps) => {
  // Deep comparison for messages array
  if (prevProps.messages.length !== nextProps.messages.length) return false;
  if (prevProps.isTyping !== nextProps.isTyping) return false;
  if (prevProps.searchQuery !== nextProps.searchQuery) return false;
  
  // Check if message content changed
  for (let i = 0; i < prevProps.messages.length; i++) {
    if (prevProps.messages[i].id !== nextProps.messages[i].id) return false;
    if (prevProps.messages[i].content !== nextProps.messages[i].content) return false;
  }
  
  // Check favorites
  if (prevProps.favorites?.length !== nextProps.favorites?.length) return false;
  
  return true;
});
